import {
  ref,
  shallowRef,
  readonly,
  computed,
  onScopeDispose,
  type Ref,
  type ComputedRef,
} from 'vue';
import type { GameSnapshot, ClientMessage } from '@/types/generated';
import type { ServerMessageMap } from './webSocketSingleton';
import type { AppGameState } from './useGameFlow';
import type { GameSession } from './useGameSession';
import STRINGS from '@/constants/strings';
import { getMetrics } from '@/telemetry/metrics';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

export interface UseGameSnapshotOptions {
  timerState: {
    startTimer: (serverStartTime: number, duration: number, onExpiry?: () => void) => void;
    stopTimer: () => void;
    isActive: Readonly<Ref<boolean>>;
  };
  gameId: Readonly<Ref<string>>;
  gameState: Readonly<Ref<AppGameState>>;
  wordSubmitted: Readonly<Ref<boolean>>;
  opponentSubmitted: Readonly<Ref<boolean>>;
  handleSnapshot: (snapshot: GameSnapshot) => void;
  handleError: (error: string) => void;
  gameSession: { save: (session: GameSession) => void; clear: () => void };
  playerNameStore: { save: (name: string) => void };
  sendMessage: (msg: ClientMessage) => boolean;
  onMessage: <T extends keyof ServerMessageMap>(
    messageType: T,
    handler: (data: ServerMessageMap[T]) => void
  ) => () => void;
}

export interface UseGameSnapshotReturn {
  currentWord: Ref<string>;
  selectionTimerDuration: Readonly<Ref<number>>;
  submissionType: Readonly<Ref<'manual' | 'auto-word' | 'auto-empty'>>;
  canSubmitWord: ComputedRef<boolean>;
  submitButtonText: ComputedRef<string>;
  currentPlayerStatus: ComputedRef<string>;
  opponentStatus: ComputedRef<string>;
  handleWordSubmit: () => void;
  resetTimerPhaseState: () => void;
}

export function useGameSnapshot(options: UseGameSnapshotOptions): UseGameSnapshotReturn {
  const {
    timerState,
    gameId,
    gameState,
    wordSubmitted,
    opponentSubmitted,
    handleSnapshot,
    handleError,
    gameSession,
    playerNameStore,
    sendMessage,
    onMessage,
  } = options;

  const selectionTimerDuration = ref(30000);
  const currentWord = ref<string>('');
  const submissionType = ref<'manual' | 'auto-empty' | 'auto-word'>('manual');

  let expiryGraceTimeout: ReturnType<typeof setTimeout> | null = null;
  let inProgressStart: number | null = null;
  // Tracks the server start_time of the currently-running selection timer.
  // StateRestored must be idempotent: receiving the same SelectingLetters +
  // active-timer snapshot must not restart the timer or re-arm the grace
  // timeout, or a server stuck in SelectingLetters drives an infinite
  // StateRefreshRequest loop (issue #104).
  let selectionTimerStartTime: number | null = null;
  // Round the last snapshot reported, so a held submission can tell whether the
  // round it was typed for is still the one in play.
  const currentRoundNumber = ref<number | null>(null);

  /**
   * A submission whose send failed because the socket was not open.
   *
   * Held rather than surfaced, and flushed from the next snapshot that confirms
   * the server is still on the same game and round, with the round still live
   * and nothing recorded for this player. Those checks are what stop a held word
   * landing somewhere it was not typed for: `current_round` does not advance
   * until the next round starts, so a `BetweenRounds` or `Finalized` snapshot
   * still carries the round number the word belongs to, and sending into it gets
   * `SubmissionAfterExpiry` back — a terminal error screen, the thing holding the
   * word exists to avoid.
   *
   * A second send is not protected by server-side idempotency: `process_submission`
   * rejects a duplicate with `GameError::Internal("Player already submitted")`
   * before `record_submission`'s HSETNX is ever reached, and that reaches the
   * player as "An internal error occurred". What keeps it to one send is clearing
   * this on a successful send, plus the `player_submitted` gate on the flush and
   * the `sentSubmission` gate below — `player_submitted` alone was not enough,
   * because it lags the send it is meant to describe (#508).
   *
   * The first failure for a round wins. A later failed attempt — the word-finding
   * timer expiring behind a failed manual submit — leaves this untouched, so the
   * `submission_time` reported back stays the moment the player actually
   * submitted. The server scores from its own receive time regardless.
   */
  interface HeldSubmission {
    gameId: string;
    roundNumber: number;
    word: string;
    submissionTime: number;
    kind: 'manual' | 'auto-word' | 'auto-empty';
  }
  let heldSubmission: HeldSubmission | null = null;

  /**
   * The round a `SubmitWord` has already gone out for on this client.
   *
   * Two things submit at the end of a round: the local timer's expiry callback,
   * and the server's `TimerEnded` snapshot a millisecond behind it. Before this,
   * the only guard between them was `wordSubmitted`, which cannot do the job —
   * it is server-authoritative, written by `applyState` from `player_submitted`,
   * so it stays false until the `PlayerSubmitted` snapshot arrives, which is
   * after both have fired. (It is stale for the snapshot in hand as well: this
   * file runs its own timer switch before delegating to `handleSnapshot`.) A
   * word submitted by hand in the last moments of the round races the same way.
   *
   * The server does not dedupe: `process_submission` rejects the second send
   * with `Internal("Player already submitted")`, which reaches the player as
   * "An internal error occurred" on the terminal error screen — for a fraction
   * of a second, until the next snapshot supersedes it (#508).
   *
   * Keyed on game and round rather than a bare flag so it cannot leak past the
   * round it belongs to, and set only on a send that actually left: a failed
   * send becomes a `heldSubmission` instead and must stay eligible for the flush.
   */
  const sentSubmission = shallowRef<{ gameId: string; roundNumber: number } | null>(null);

  const alreadySentThisRound = (): boolean =>
    sentSubmission.value !== null &&
    sentSubmission.value.gameId === gameId.value &&
    sentSubmission.value.roundNumber === currentRoundNumber.value;

  const clearExpiryGraceTimeout = () => {
    if (expiryGraceTimeout !== null) {
      clearTimeout(expiryGraceTimeout);
      expiryGraceTimeout = null;
    }
  };

  const startGraceTimeout = (ms: number) => {
    clearExpiryGraceTimeout();
    expiryGraceTimeout = setTimeout(() => {
      expiryGraceTimeout = null;
      if (gameId.value) {
        const sent = sendMessage({ StateRefreshRequest: { game_id: gameId.value } });
        if (!sent) {
          // useConnectionGiveUp is the sole owner of connection loss and its
          // window is already running by the time a send can fail, so this path
          // deliberately raises nothing in the UI. Log for diagnostics only.
          reportClientError(
            new Error(`Grace timeout: StateRefreshRequest failed to send (gameId=${gameId.value})`),
            ErrorLocation.GameSnapshotGraceTimeout
          );
        }
      } else {
        reportClientError(
          new Error('Grace timeout fired but gameId is null'),
          ErrorLocation.GameSnapshotGraceTimeout
        );
      }
    }, ms);
  };

  const resetTimerPhaseState = () => {
    clearExpiryGraceTimeout();
    currentWord.value = '';
    submissionType.value = 'manual';
    inProgressStart = null;
    selectionTimerStartTime = null;
    heldSubmission = null;
    sentSubmission.value = null;
  };

  // `alreadySentThisRound` and not just `wordSubmitted`, for the same reason
  // `trySubmitWord` needs both: `wordSubmitted` is server-authoritative and
  // stays false for a round trip after the word has left. Without it the
  // button stays enabled over that window and the press does nothing.
  const canSubmitWord = computed(() => {
    return (
      timerState.isActive.value &&
      currentWord.value.trim().length > 0 &&
      !wordSubmitted.value &&
      !alreadySentThisRound()
    );
  });

  const submitButtonText = computed(() => {
    if (wordSubmitted.value || alreadySentThisRound()) {
      return submissionType.value === 'manual'
        ? STRINGS.ui.buttons.submitted
        : STRINGS.ui.buttons.autoSubmitted;
    }
    if (!timerState.isActive.value) return STRINGS.ui.buttons.timeUp;
    return STRINGS.ui.buttons.submit;
  });

  const currentPlayerStatus = computed(() => {
    if (wordSubmitted.value) {
      if (submissionType.value === 'auto-word') return STRINGS.status.player.autoSubmittedWord;
      if (submissionType.value === 'auto-empty') return STRINGS.status.player.autoSubmittedEmpty;
      return STRINGS.status.player.submitted;
    }
    if (currentWord.value.trim().length > 0) return STRINGS.status.player.typing;
    if (timerState.isActive.value) return STRINGS.status.player.thinking;
    return STRINGS.status.player.ready;
  });

  const opponentStatus = computed(() => {
    if (opponentSubmitted.value) return STRINGS.status.player.submitted;
    return STRINGS.status.player.thinking;
  });

  /**
   * Attempts to submit a word.
   *
   * Returns without action once this round is settled for this player, by
   * either measure: the server has recorded a submission (`wordSubmitted`), or
   * one has already left this client for this round (`sentSubmission`). Both
   * are needed — see `sentSubmission`.
   */
  const trySubmitWord = (
    word: string,
    kind: 'manual' | 'auto-word' | 'auto-empty',
    submissionTime = Date.now()
  ) => {
    if (wordSubmitted.value || alreadySentThisRound()) {
      return;
    }
    submissionType.value = kind;

    if (!gameId.value || typeof gameId.value !== 'string') {
      reportClientError(
        new Error(`Invalid game ID for word submission: kind=${kind}`),
        ErrorLocation.GameSnapshotWordSubmitInvalidId
      );
      handleError('Invalid game state');
      return;
    }

    const success = sendMessage({
      SubmitWord: {
        game_id: gameId.value,
        word,
        submission_time: submissionTime,
      },
    });

    if (success) {
      if (currentRoundNumber.value !== null) {
        sentSubmission.value = { gameId: gameId.value, roundNumber: currentRoundNumber.value };
      } else {
        // The word left with no round to key the marker on, so nothing stops a
        // second send for this round — the #508 duplicate, unguarded. Every
        // path into here runs off a snapshot that sets the round first, so
        // this should not be reachable; it is reported rather than passed
        // over, the way the symmetric failure path counts `no_round_number`.
        reportClientError(
          new Error(`Word submitted with no round number: kind=${kind}`),
          ErrorLocation.GameSnapshotSentSubmissionUnkeyed
        );
      }
      // Any successful send settles the round for this player, so a word held
      // from an earlier failure has nothing left to do. Without this, a
      // reconnect whose StateRestored auto-submits would send the word again
      // right behind it.
      heldSubmission = null;
      // Record word submission duration (time from in-progress start to submit)
      if (inProgressStart && kind === 'manual') {
        getMetrics().uiWordSubmissionDurationMs.record(Date.now() - inProgressStart);
      }
      return;
    }

    if (kind !== 'manual') {
      getMetrics().gameAutoSubmitFailures.add(1);
    }
    reportClientError(
      new Error(`Failed to submit word: kind=${kind}`),
      ErrorLocation.GameSnapshotWordSubmitFailed
    );
    // Held for the reconnect rather than raised as a terminal error. A send
    // fails whenever the socket is not open, which in a pod loss is the moment
    // before a reconnect lands, and `useConnectionGiveUp` is the sole authority
    // on when to stop waiting for one — the same reasoning `startGraceTimeout`
    // above already follows. Ending the game here did the opposite: it unmounted
    // the game components, which dropped the manager's refCount to zero and
    // cancelled the reconnect that was already scheduled, so the player was
    // stranded on a terminal screen by an outage the app was moments from
    // recovering from.
    //
    // The word is held, not guaranteed. `handleClose` schedules nothing once
    // `reconnectAttempts` is spent or the last reference is released, and a held
    // word only ever leaves on a snapshot, so a socket that never comes back
    // drops it — counted below rather than silently.
    if (currentRoundNumber.value === null) {
      getMetrics().gameHeldSubmissionDropped.add(1, { reason: 'no_round_number' });
      return;
    }
    if (
      heldSubmission &&
      heldSubmission.gameId === gameId.value &&
      heldSubmission.roundNumber === currentRoundNumber.value
    ) {
      // First failure for the round wins. Re-point the status text at what will
      // actually be sent, so the aria-label does not claim an auto-submission
      // for a word the player submitted by hand.
      submissionType.value = heldSubmission.kind;
      return;
    }
    heldSubmission = {
      gameId: gameId.value,
      roundNumber: currentRoundNumber.value,
      word,
      submissionTime,
      kind,
    };
  };

  /**
   * Sends a held submission once a snapshot shows it is still the right one to
   * send, and discards it once it is not.
   */
  const flushHeldSubmission = (state: GameSnapshot['state']) => {
    const held = heldSubmission;
    if (!held) return;
    heldSubmission = null;

    if (state.game_id !== held.gameId || state.round_number !== held.roundNumber) {
      // The round moved on without it. Sending now would record the word
      // against a round it was not typed for.
      getMetrics().gameHeldSubmissionDropped.add(1, { reason: 'round_moved_on' });
      return;
    }
    if (state.player_submitted) {
      getMetrics().gameHeldSubmissionDropped.add(1, { reason: 'already_recorded' });
      return;
    }
    // The round number alone does not say the round is still open — it stays put
    // through RoundCompleted, BetweenRounds and Finalized. The server only
    // publishes a `timer` while its round timer is Active, so this is the check
    // that the submission would still be accepted; without it the flush lands on
    // `SubmissionAfterExpiry` and the player gets the error screen anyway.
    //
    // Nothing is lost by being strict here. The one case this turns away that is
    // still submittable — InProgress with the timer expired on reconnect — is
    // already handled by the StateRestored branch below, which runs first and
    // clears the held word on its way through.
    if (state.status !== 'InProgress' || !state.timer?.is_active) {
      getMetrics().gameHeldSubmissionDropped.add(1, { reason: 'round_not_live' });
      return;
    }

    trySubmitWord(held.word, held.kind, held.submissionTime);
  };

  /**
   * Auto-submission handler - called when timer expires
   */
  const handleAutoSubmission = () => {
    const word = currentWord.value?.trim() || '';
    trySubmitWord(word, word.length > 0 ? 'auto-word' : 'auto-empty');
  };

  /**
   * Manual word submission triggered by user action
   */
  const handleWordSubmit = () => {
    const word = currentWord.value?.trim();
    if (!word || !timerState.isActive.value) return;
    trySubmitWord(word, 'manual');
  };

  const unsubscribeFunctions: (() => void)[] = [];

  // Unified GameSnapshot handler
  unsubscribeFunctions.push(
    onMessage('GameSnapshot', (snapshot: GameSnapshot) => {
      const { event, state } = snapshot;
      currentRoundNumber.value = state.round_number;

      // Handle timer-related side effects before delegating to useGameFlow
      switch (event) {
        case 'LetterSelected':
          // Start selection timer if present and in selection phase
          if (
            state.timer &&
            state.timer.is_active &&
            state.status === 'SelectingLetters' &&
            !timerState.isActive.value
          ) {
            selectionTimerDuration.value = state.timer.duration;
            selectionTimerStartTime = state.timer.start_time;
            timerState.startTimer(state.timer.start_time, state.timer.duration, () => {
              // Server auto-fills remaining letters on selection timer expiry.
              // Grace period: if no SelectionComplete/TimerStarted arrives within
              // 200ms, request a state refresh (the notification may have been lost).
              startGraceTimeout(200);
            });
          }
          break;

        // A bulk auto-selection (bot / "pick for me" / timer auto-fill) is
        // always a completed selection, so it shares SelectionComplete's timer
        // side-effects: cancel the expiry grace and stop the selection timer.
        case 'SelectionComplete':
        case 'LettersBulkSelected':
          clearExpiryGraceTimeout();
          selectionTimerStartTime = null;
          // Stop selection timer (word-finding timer starts on TimerStarted)
          timerState.stopTimer();
          break;

        case 'TimerStarted':
          if (state.timer && state.timer.is_active) {
            inProgressStart = Date.now();
            selectionTimerStartTime = null;
            timerState.startTimer(state.timer.start_time, state.timer.duration, () => {
              handleAutoSubmission();
              // Start grace period: if no RoundCompleted arrives within 3s,
              // request a state refresh from the server. TimerEnded does not
              // satisfy it — see the TimerEnded case below.
              startGraceTimeout(3000);
            });
            // Only reset word UI if we haven't already transitioned to in-progress
            // (SelectionComplete arrives first and the user may have started typing)
            if (gameState.value !== 'in-progress') {
              currentWord.value = '';
              submissionType.value = 'manual';
            }
          } else {
            reportClientError(
              new Error('TimerStarted received but timer is missing or inactive'),
              ErrorLocation.GameSnapshotTimerStarted
            );
            startGraceTimeout(200);
          }
          break;

        case 'TimerEnded':
          // Re-arm, do not clear. The round is not over until RoundCompleted:
          // the server sends TimerEnded first, then waits out its submission
          // grace period, then writes the round to PostgreSQL and Redis. A task
          // that dies anywhere from here on strands the round, and that is the
          // larger half of #485 — clearing here left it with no trigger at all,
          // because this probe is the only thing that asks the server to
          // re-drive a round its timer task abandoned.
          //
          // startGraceTimeout clears the pending one first, so this replaces
          // rather than stacks. Every event that ends the round or the game —
          // RoundCompleted, GameFinalized, OpponentEndedGame, ServerError, a
          // Finalized StateRestored — cancels it, and it fires once.
          startGraceTimeout(3000);
          if (gameState.value === 'in-progress') {
            handleAutoSubmission();
          }
          timerState.stopTimer();
          break;

        case 'StateRestored':
          // The only snapshot allowed to drop the marker, because it is the only
          // one whose `player_submitted` can be newer than our own send. A
          // `StateRefreshRequest` travels the same connection, and the receive
          // loop (`handlers/websocket.rs`) awaits `dispatch_client_message`
          // before reading the next message, so the server finishes a
          // `SubmitWord` before it starts a refresh sent behind it. `TimerEnded`
          // has no such ordering — it is published by a timer task running
          // alongside — so `TimerEnded` saying "not submitted" is not evidence.
          //
          // A reconnect is weaker: it arrives on a new connection whose task runs
          // concurrently with the old connection's still-in-flight `SubmitWord`,
          // so its `player_submitted` may simply predate that send. The marker is
          // dropped anyway, because from here a word lost with the socket and one
          // still in flight look identical, and only the first is recoverable.
          // The cost of guessing wrong is the #508 flash on a path that already
          // had it; the cost of not guessing is a word silently lost.
          //
          // Keyed like every other check on `sentSubmission`: a restore for a
          // different game or an earlier round says nothing about the send in
          // flight for the round actually in play.
          if (
            !state.player_submitted &&
            sentSubmission.value !== null &&
            state.game_id === sentSubmission.value.gameId &&
            state.round_number === sentSubmission.value.roundNumber
          ) {
            sentSubmission.value = null;
          }
          // Must precede the timer restore below. Finalizing a game rewrites only
          // the game meta, so a game the reaper reaped mid-round comes back with
          // its round timer still Active — leaving the local timer to run out,
          // fire handleAutoSubmission, and have the rejected SubmitWord replace
          // game-over with the error screen.
          if (state.status === 'Finalized') {
            clearExpiryGraceTimeout();
            timerState.stopTimer();
            break;
          }
          // Restore timer if active
          if (state.timer && state.timer.is_active) {
            if (state.status === 'InProgress') {
              selectionTimerStartTime = null;
              timerState.startTimer(
                state.timer.start_time,
                state.timer.duration,
                handleAutoSubmission
              );
            } else if (state.status === 'SelectingLetters') {
              // Idempotency guard: if we've already started a selection timer
              // with this same server start_time, do not restart it or re-arm
              // the grace timeout. Without this, a server stuck in
              // SelectingLetters echoes StateRestored on every refresh and the
              // client spins in a ~200ms StateRefreshRequest loop (issue #104).
              //
              // Key on start_time alone — do NOT conjunct with
              // `timerState.isActive.value`. Once the local timer expires
              // (server start_time > duration in the past), `isActive` flips
              // false, and a same-start_time echo would fall through, restart
              // the timer, fire the grace, and send StateRefreshRequest.
              if (selectionTimerStartTime === state.timer.start_time) {
                break;
              }
              selectionTimerDuration.value = state.timer.duration;
              selectionTimerStartTime = state.timer.start_time;
              timerState.startTimer(state.timer.start_time, state.timer.duration, () => {
                // Same grace-period logic as the LetterSelected handler
                startGraceTimeout(200);
              });
            } else {
              reportClientError(
                new Error(`StateRestored: active timer in unexpected status: ${state.status}`),
                ErrorLocation.GameSnapshotStateRestored
              );
            }
          } else if (state.status === 'InProgress') {
            console.debug('[useGameSnapshot] StateRestored: timer already expired on reconnect', {
              status: state.status,
            });
            if (!state.player_submitted) {
              handleAutoSubmission();
            }
          }
          break;

        case 'NewRoundRequested':
          break;

        case 'RoundCompleted':
          clearExpiryGraceTimeout();
          break;

        case 'GameFinalized':
        case 'OpponentEndedGame':
        case 'ServerError':
          clearExpiryGraceTimeout();
          gameSession.clear();
          break;

        case 'GameStarted': {
          // Save session for reconnection
          gameSession.save({
            game_id: state.game_id,
            player_id: state.player_id,
          });
          // Persist player name separately (survives game session clears)
          const player = state.players.find(p => p.id === state.player_id);
          if (player) {
            playerNameStore.save(player.name);
          } else {
            reportClientError(
              new Error('GameStarted: player_id not found in players list'),
              ErrorLocation.GameSnapshotPlayerNotFound
            );
          }
          break;
        }
      }

      // Delegate to useGameFlow for state management
      // Note: no messages arrive before onMounted completes, so handleSnapshot
      // is always available when this handler fires.
      handleSnapshot(snapshot);

      // Post-snapshot: start selection timer for NewRoundStarting
      // (must happen after handleSnapshot which resets game state and stops timer)
      if (event === 'NewRoundStarting' && state.timer && state.timer.is_active) {
        selectionTimerDuration.value = state.timer.duration;
        selectionTimerStartTime = state.timer.start_time;
        timerState.startTimer(state.timer.start_time, state.timer.duration);
      }

      // Last, so the flush sees the app state this snapshot just produced. Any
      // snapshot will do — the point is that one only arrives over a live
      // socket, and the first one after a reconnect is the StateRestored that
      // says whether the held word is still the right one to send.
      flushHeldSubmission(state);
    })
  );

  unsubscribeFunctions.push(
    onMessage('SubmissionResult', data => {
      if (!data.accepted) {
        getMetrics().gameActionRejected.add(1);
        submissionType.value = 'manual';
        if (!data.reason) {
          reportClientError(
            new Error('SubmissionResult rejected with no reason from server'),
            ErrorLocation.GameSnapshotSubmissionRejectedNoReason
          );
        }
      }
    })
  );

  // The reaper finalized an idle game under the player. It carries only the game_id,
  // so ask for a snapshot rather than guessing at a screen: the reply is a
  // StateRestored carrying the real Finalized status and round results, which
  // useGameFlow already maps to game-over.
  unsubscribeFunctions.push(
    onMessage('GameFinalizedInactivity', data => {
      // A notification for a game the player has already left is expected — they
      // moved on and the reaper caught up later. Nothing to resync.
      if (!gameId.value || data.game_id !== gameId.value) return;
      const sent = sendMessage({ StateRefreshRequest: { game_id: gameId.value } });
      if (!sent) {
        // The socket is down; the reconnect will deliver a snapshot. Log for
        // diagnostics only.
        reportClientError(
          new Error(
            `GameFinalizedInactivity: StateRefreshRequest failed to send (gameId=${gameId.value})`
          ),
          ErrorLocation.GameSnapshotInactivityRefreshFailed
        );
      }
    })
  );

  onScopeDispose(() => {
    unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    clearExpiryGraceTimeout();
  });

  return {
    currentWord,
    selectionTimerDuration: readonly(selectionTimerDuration),
    submissionType: readonly(submissionType),
    canSubmitWord,
    submitButtonText,
    currentPlayerStatus,
    opponentStatus,
    handleWordSubmit,
    resetTimerPhaseState,
  };
}
